import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { extractEmployees, taskBelongsToEmployee, cellText, calculatePerformanceRating, normalizeToMs } from "../../utils/helpers";
import * as XLSX from "xlsx";

export default function GenerateReportPage() {
  const { adminRecords, userRecords, larkUsers, deletedUsers, loading } = useData();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const reportData = useMemo(() => {
    const now = Date.now();
    const startMs = startDate ? new Date(startDate + "T00:00:00").getTime() : null;
    const endMs = endDate ? new Date(endDate + "T23:59:59").getTime() : null;
    // Employees deleted in Lark drop out once the range starts after they left.
    // With no start date the range is all-time, so their history still counts.
    const emps = extractEmployees(adminRecords, { larkUsers, deletedUsers, periodStartMs: startMs, includeAllActive: true });

    // Admin-done lookup: task fully done when all users completed
    const adm = {};
    for (const ar of adminRecords) {
      const tid = cellText(ar.fields?.["Task ID"]);
      if (!tid || tid === "—") continue;
      const st = cellText(ar.fields?.["Overall Status"]).toLowerCase();
      adm[tid] = st.includes("done") || st.includes("✅");
    }
    const isFullyDone = (t) => !!adm[cellText(t.fields?.["Task ID"])];

    return emps.map(emp => {
      // Find tasks assigned to this employee
      const empTasks = userRecords.filter(r => taskBelongsToEmployee(r, emp));

      // Filter tasks by date range (End Date/Due Date is the primary indicator)
      let filteredTasks = empTasks;
      if (startMs || endMs) {
        filteredTasks = filteredTasks.filter(t => {
          const dueTime = normalizeToMs(t.fields?.["End Date"]);
          if (startMs && (!dueTime || dueTime < startMs)) return false;
          if (endMs && (!dueTime || dueTime > endMs)) return false;
          return true;
        });
      }

      // Calculate stats — "done" means all assigned users completed
      const total = filteredTasks.length;
      const completed = filteredTasks.filter(t => isFullyDone(t)).length;
      const overdue = filteredTasks.filter(t => !isFullyDone(t) && normalizeToMs(t.fields?.["End Date"]) > 0 && normalizeToMs(t.fields?.["End Date"]) < now).length;
      const pending = filteredTasks.filter(t => !isFullyDone(t) && (normalizeToMs(t.fields?.["End Date"]) === 0 || normalizeToMs(t.fields?.["End Date"]) >= now)).length;

      // Completed Within/After Time
      let completedWithin = 0;
      let completedAfter = 0;
      filteredTasks.forEach(t => {
        const f = t.fields || {}, isDone = isFullyDone(t);
        if (isDone) {
          const dueTime = normalizeToMs(f["End Date"]);
          const compTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);
          if (compTime <= (dueTime + 60000) || dueTime === 0) {
            completedWithin++;
          } else {
            completedAfter++;
          }
        }
      });

      // Calculate performance rating
      const ratingVal = calculatePerformanceRating(filteredTasks, adminRecords);
      const performanceRatio = total > 0 ? ratingVal.toFixed(2) : "-";

      return {
        name: emp.name,
        total,
        completed,
        pending,
        overdue,
        completedWithin,
        completedAfter,
        performanceRatio
      };
    });
  }, [adminRecords, userRecords, larkUsers, deletedUsers, startDate, endDate]);

  // ── Decorative Excel Export ──────────────────────────────────────────────
  const handleExport = () => {
    const HEADER_FILL   = { patternType: "solid", fgColor: { rgb: "1E3A5F" } };   // deep navy
    const HEADER_FONT   = { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Calibri" };
    const SUBHEADER_FILL= { patternType: "solid", fgColor: { rgb: "2E86C1" } };   // medium blue
    const SUBHEADER_FONT= { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Calibri" };
    const ODD_FILL      = { patternType: "solid", fgColor: { rgb: "EBF5FB" } };   // light blue
    const EVEN_FILL     = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const SUMMARY_FILL  = { patternType: "solid", fgColor: { rgb: "1A5276" } };   // dark blue
    const SUMMARY_FONT  = { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Calibri" };
    const GOOD_FONT     = { bold: true, color: { rgb: "1E8449" }, sz: 10, name: "Calibri" };  // green
    const WARN_FONT     = { bold: true, color: { rgb: "B7950B" }, sz: 10, name: "Calibri" };  // amber
    const BAD_FONT      = { bold: true, color: { rgb: "922B21" }, sz: 10, name: "Calibri" };  // red
    const CELL_FONT     = { sz: 10, name: "Calibri" };
    const BOLD_FONT     = { bold: true, sz: 10, name: "Calibri" };
    const BORDER        = {
      top:    { style: "thin", color: { rgb: "BDC3C7" } },
      bottom: { style: "thin", color: { rgb: "BDC3C7" } },
      left:   { style: "thin", color: { rgb: "BDC3C7" } },
      right:  { style: "thin", color: { rgb: "BDC3C7" } }
    };
    const THICK_BORDER  = {
      top:    { style: "medium", color: { rgb: "1E3A5F" } },
      bottom: { style: "medium", color: { rgb: "1E3A5F" } },
      left:   { style: "medium", color: { rgb: "1E3A5F" } },
      right:  { style: "medium", color: { rgb: "1E3A5F" } }
    };

    const CENTER = { horizontal: "center", vertical: "center" };
    const LEFT   = { horizontal: "left",   vertical: "center" };

    // Helper to create a styled cell
    const cell = (v, font, fill, alignment, border) => ({
      v,
      t: typeof v === "number" ? "n" : "s",
      s: { font: font || CELL_FONT, fill: fill || {}, alignment: alignment || LEFT, border: border || BORDER }
    });

    const wb = XLSX.utils.book_new();

    // ── Row 1: Report title (merged A1:I1) ──────────────────────────────
    const dateRange = (startDate || endDate)
      ? `  |  Period: ${startDate || "—"}  →  ${endDate || "—"}`
      : "";
    const titleText = `EMPLOYEE PERFORMANCE REPORT${dateRange}`;

    // ── Row 2: Column headers ────────────────────────────────────────────
    const COLS = [
      "Sr. No.",
      "Employee Name",
      "Total Tasks",
      "Completed Tasks",
      "Pending Tasks",
      "Overdue Tasks",
      "Completed Within Time",
      "Completed After Time",
      "Performance Ratio"
    ];

    // Build aoa (array of arrays of cell objects)
    const aoa = [];

    // Row 0 — Title row
    const titleRow = [
      { v: titleText, t: "s", s: {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" }, name: "Calibri" },
        fill: HEADER_FILL,
        alignment: { horizontal: "center", vertical: "center" },
        border: THICK_BORDER
      } }
    ];
    // Pad the rest of the title row with empty styled cells so merging works
    for (let i = 1; i < COLS.length; i++) {
      titleRow.push({ v: "", t: "s", s: { fill: HEADER_FILL, border: THICK_BORDER } });
    }
    aoa.push(titleRow);

    // Row 1 — Column headers
    aoa.push(COLS.map(h => cell(h, SUBHEADER_FONT, SUBHEADER_FILL, CENTER, BORDER)));

    // Data rows
    reportData.forEach((row, idx) => {
      const isOdd = idx % 2 === 0;
      const rowFill = isOdd ? ODD_FILL : EVEN_FILL;

      const perfNum = parseFloat(row.performanceRatio);
      const perfFont = row.performanceRatio === "-"
        ? { ...CELL_FONT, bold: true, color: { rgb: "7F8C8D" } }
        : perfNum >= 7 ? GOOD_FONT
        : perfNum >= 4 ? WARN_FONT
        : BAD_FONT;

      aoa.push([
        cell(idx + 1,                   { ...BOLD_FONT, color: { rgb: "5D6D7E" } }, rowFill, CENTER, BORDER),
        cell(row.name,                  BOLD_FONT,                                  rowFill, LEFT,   BORDER),
        cell(row.total,                 CELL_FONT,                                  rowFill, CENTER, BORDER),
        cell(row.completed,             { ...BOLD_FONT, color: { rgb: "1E8449" } }, rowFill, CENTER, BORDER),
        cell(row.pending,               { ...BOLD_FONT, color: { rgb: "B7950B" } }, rowFill, CENTER, BORDER),
        cell(row.overdue,               { ...BOLD_FONT, color: { rgb: "922B21" } }, rowFill, CENTER, BORDER),
        cell(row.completedWithin,       { ...BOLD_FONT, color: { rgb: "1A5276" } }, rowFill, CENTER, BORDER),
        cell(row.completedAfter,        { ...BOLD_FONT, color: { rgb: "784212" } }, rowFill, CENTER, BORDER),
        cell(row.performanceRatio === "-" ? "—" : Number(row.performanceRatio), perfFont, rowFill, CENTER, BORDER),
      ]);
    });

    // Summary / totals row
    const sumTotal     = reportData.reduce((a, r) => a + r.total, 0);
    const sumCompleted = reportData.reduce((a, r) => a + r.completed, 0);
    const sumPending   = reportData.reduce((a, r) => a + r.pending, 0);
    const sumOverdue   = reportData.reduce((a, r) => a + r.overdue, 0);
    const sumWithin    = reportData.reduce((a, r) => a + r.completedWithin, 0);
    const sumAfter     = reportData.reduce((a, r) => a + r.completedAfter, 0);
    const avgPerf      = reportData.filter(r => r.performanceRatio !== "-").length > 0
      ? (reportData.reduce((a, r) => a + (r.performanceRatio !== "-" ? parseFloat(r.performanceRatio) : 0), 0) /
         reportData.filter(r => r.performanceRatio !== "-").length).toFixed(2)
      : "—";

    aoa.push([
      cell("",          SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell("TOTAL / AVG", SUMMARY_FONT, SUMMARY_FILL, LEFT,   THICK_BORDER),
      cell(sumTotal,    SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(sumCompleted,SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(sumPending,  SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(sumOverdue,  SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(sumWithin,   SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(sumAfter,    SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
      cell(avgPerf === "—" ? "—" : Number(avgPerf), SUMMARY_FONT, SUMMARY_FILL, CENTER, THICK_BORDER),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Merge title row across all columns (A1:I1)
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } }];

    // Column widths
    ws["!cols"] = [
      { wch: 8  },   // Sr. No.
      { wch: 28 },   // Employee Name
      { wch: 14 },   // Total Tasks
      { wch: 18 },   // Completed
      { wch: 15 },   // Pending
      { wch: 15 },   // Overdue
      { wch: 24 },   // Within Time
      { wch: 22 },   // After Time
      { wch: 18 },   // Performance Ratio
    ];

    // Row heights
    ws["!rows"] = [
      { hpt: 32 },  // title row
      { hpt: 22 },  // header row
      ...reportData.map(() => ({ hpt: 20 })),
      { hpt: 22 },  // summary row
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Performance Report");

    const dateStr = (startDate || endDate) ? `_${startDate || ""}_to_${endDate || ""}` : "";
    XLSX.writeFile(wb, `Employee_Performance_Report${dateStr}.xlsx`);
  };

  if (loading && adminRecords.length === 0)
    return <div className="loading-box"><div className="spinner" /><span>Loading report...</span></div>;

  const hasDateFilter = startDate || endDate;

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1>📈 Generate Report</h1>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleExport}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px" }}
        >
          📥 Export to Excel
        </button>
      </div>

      {/* ── Toolbar: only date range + reset ── */}
      <div className="toolbar" style={{ marginBottom: 20, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div className="date-range-group" style={{ flex: 2 }}>
          <input
            type="date"
            className={`date-input ${startDate ? "has-value" : ""}`}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
          <span style={{ color: "var(--gray-400)", fontSize: 12 }}>to</span>
          <input
            type="date"
            className={`date-input ${endDate ? "has-value" : ""}`}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </div>

        {/* Reset Dates button — only visible when a date is set */}
        {hasDateFilter && (
          <button
            className="btn"
            onClick={() => { setStartDate(""); setEndDate(""); }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", fontSize: 13,
              border: "1.5px solid var(--gray-200)",
              borderRadius: "8px",
              background: "var(--surface-1)",
              color: "var(--gray-700)",
              cursor: "pointer",
              whiteSpace: "nowrap"
            }}
          >
            ↺ Reset Dates
          </button>
        )}
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "center" }}>Sr. No.</th>
                <th>Employee Name</th>
                <th style={{ textAlign: "center" }}>Total Tasks</th>
                <th style={{ textAlign: "center" }}>Completed Tasks</th>
                <th style={{ textAlign: "center" }}>Pending Tasks</th>
                <th style={{ textAlign: "center" }}>Overdue Tasks</th>
                <th style={{ textAlign: "center" }}>Completed Within Time</th>
                <th style={{ textAlign: "center" }}>Completed After Time</th>
                <th style={{ textAlign: "center" }}>Performance Ratio</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length > 0 ? (
                reportData.map((row, index) => (
                  <tr key={row.name}>
                    <td style={{ textAlign: "center", fontWeight: 600, color: "var(--gray-400)" }}>{index + 1}</td>
                    <td style={{ fontWeight: 600, color: "var(--gray-900)" }}>{row.name}</td>
                    <td style={{ textAlign: "center", fontWeight: 500 }}>{row.total}</td>
                    <td style={{ textAlign: "center", color: "var(--success)", fontWeight: 600 }}>{row.completed}</td>
                    <td style={{ textAlign: "center", color: "var(--warning)", fontWeight: 600 }}>{row.pending}</td>
                    <td style={{ textAlign: "center", color: "var(--danger)", fontWeight: 600 }}>{row.overdue}</td>
                    <td style={{ textAlign: "center", color: "var(--primary)", fontWeight: 600 }}>{row.completedWithin}</td>
                    <td style={{ textAlign: "center", color: "var(--orange)", fontWeight: 600 }}>{row.completedAfter}</td>
                    <td style={{
                      textAlign: "center",
                      fontWeight: 700,
                      color: row.performanceRatio === "-" ? "var(--gray-400)" : (parseFloat(row.performanceRatio) >= 7 ? "var(--success)" : (parseFloat(row.performanceRatio) >= 4 ? "var(--warning)" : "var(--danger)"))
                    }}>
                      {row.performanceRatio}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: "40px", color: "var(--gray-400)" }}>
                    No employees found in the system.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
